<script>
import CrmDealsAPI from 'dashboard/api/crmDeals';
import { useAlert } from 'dashboard/composables';

// Requisitos que a etapa cobra para o negócio poder SAIR dela: campos que
// precisam estar preenchidos e um checklist livre. (A trava é na saída — a
// etapa que cobra é a de onde o negócio está saindo; ver Crm::Deal#gating_stage.)
//
// Com `target: 'win'` o mesmo painel edita os requisitos do FUNIL para marcar
// como ganho (crm_funnels.win_requirements): mesma lista de campos, mesmo
// formato, outro lugar de gravação.
//
// A lista de campos vem do servidor (Crm::Stages::RequirementChecker), e não
// de uma cópia aqui, pra exigência nova aparecer no cadastro sozinha.
export default {
  name: 'StageRequirementsPanel',
  props: {
    funnelId: { type: [String, Number], required: true },
    // Etapa editada (target 'stage'); ignorada no target 'win'.
    stage: { type: Object, default: null },
    target: {
      type: String,
      default: 'stage',
      validator: value => ['stage', 'win'].includes(value),
    },
    // Requisitos atuais do funil, no target 'win'.
    winRequirements: { type: Object, default: () => ({}) },
  },
  emits: ['saved', 'close'],
  data() {
    return {
      fields: [],
      selectedFields: [],
      checklist: [],
      newItem: '',
      isLoading: true,
      isSaving: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const { data } = await CrmDealsAPI.requirementFields(this.funnelId);
        this.fields = data.fields || [];

        const requirements =
          this.target === 'win'
            ? this.winRequirements || {}
            : this.stage?.requirements || {};
        this.selectedFields = [...(requirements.fields || [])];
        this.checklist = [...(requirements.checklist || [])];
      } catch (error) {
        useAlert('Não foi possível carregar os campos exigíveis');
      } finally {
        this.isLoading = false;
      }
    },
    // A lista traz campo do negócio e do contato; agrupar evita "Nome" e
    // "Título" parecerem a mesma coisa.
    groupedFields() {
      const groups = [];
      this.fields.forEach(field => {
        const name = field.group || 'Outros';
        const existing = groups.find(group => group.name === name);
        if (existing) existing.fields.push(field);
        else groups.push({ name, fields: [field] });
      });
      return groups;
    },
    toggleField(key) {
      this.selectedFields = this.selectedFields.includes(key)
        ? this.selectedFields.filter(item => item !== key)
        : [...this.selectedFields, key];
    },
    addItem() {
      const item = this.newItem.trim();
      if (!item || this.checklist.includes(item)) return;

      this.checklist = [...this.checklist, item];
      this.newItem = '';
    },
    removeItem(item) {
      this.checklist = this.checklist.filter(entry => entry !== item);
    },
    async save() {
      this.isSaving = true;
      const requirements = {
        fields: this.selectedFields,
        checklist: this.checklist,
      };
      try {
        if (this.target === 'win') {
          await CrmDealsAPI.updateFunnel(this.funnelId, {
            win_requirements: requirements,
          });
        } else {
          await CrmDealsAPI.updateStage(this.funnelId, this.stage.id, {
            requirements,
          });
        }
        useAlert('Requisitos salvos');
        this.$emit('saved');
      } catch (error) {
        useAlert('Não foi possível salvar os requisitos');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-md space-y-3">
    <div>
      <template v-if="target === 'win'">
        <p class="text-sm font-semibold text-n-slate-12 m-0">
          Requisitos para marcar como ganho
        </p>
        <p class="text-xs text-n-slate-11 m-0 mt-0.5">
          Cobrados em qualquer etapa na hora de marcar o negócio como ganho,
          além do que a etapa atual já exige. Automação e importação não são
          barradas.
        </p>
      </template>
      <template v-else>
        <p class="text-sm font-semibold text-n-slate-12 m-0">
          Requisitos para sair de "{{ stage.name }}"
        </p>
        <p class="text-xs text-n-slate-11 m-0 mt-0.5">
          Vale para quem move o negócio na tela. Automação e importação não são
          barradas, e negócio que já está aqui não é cobrado.
        </p>
      </template>
    </div>

    <p v-if="isLoading" class="text-xs text-n-slate-11 m-0">Carregando...</p>

    <template v-else>
      <div>
        <p
          class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide m-0 mb-1"
        >
          Campos obrigatórios
        </p>
        <div v-for="group in groupedFields()" :key="group.name" class="mb-2">
          <p
            class="text-[10px] font-semibold text-n-slate-10 uppercase m-0 mb-1"
          >
            {{ group.name }}
          </p>
          <div class="grid grid-cols-2 gap-1">
            <label
              v-for="field in group.fields"
              :key="field.key"
              class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedFields.includes(field.key)"
                class="cursor-pointer"
                @change="toggleField(field.key)"
              />
              {{ field.label }}
            </label>
          </div>
        </div>
      </div>

      <div>
        <p
          class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide m-0 mb-1"
        >
          Checklist
        </p>
        <div v-if="checklist.length" class="space-y-1 mb-2">
          <div
            v-for="item in checklist"
            :key="item"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <span class="i-lucide-check-square w-3.5 h-3.5 text-n-slate-10" />
            <span class="flex-1 truncate">{{ item }}</span>
            <button
              class="text-xs text-n-ruby-11 hover:underline border-none bg-transparent cursor-pointer px-0"
              @click="removeItem(item)"
            >
              remover
            </button>
          </div>
        </div>
        <div class="flex gap-2">
          <input
            v-model="newItem"
            type="text"
            placeholder="Ex.: Contrato assinado"
            class="flex-1 px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
            @keyup.enter="addItem"
          />
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-2 cursor-pointer"
            @click="addItem"
          >
            Adicionar
          </button>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-1">
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 cursor-pointer"
          @click="$emit('close')"
        >
          Fechar
        </button>
        <button
          class="px-3 py-1.5 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500 hover:bg-woot-600 disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          {{ isSaving ? 'Salvando...' : 'Salvar requisitos' }}
        </button>
      </div>
    </template>
  </div>
</template>
